import React, { useState } from "react";
import { useLazyGetRunContextSegmentQuery } from "../../../app/api/console/agenticRunsApiSlice";
import { CONTEXT_CATEGORIES, formatTokenCount } from "./contextUsage";
import { ProseText } from "./TimelineMarkdown";

/**
 * What was in this run's system prompt, and where each piece came from.
 *
 * The trace shows what an agent *did*; this shows what it was *given*. Sits
 * next to the run's "prompt" disclosure and stays collapsed by default —
 * the timeline is the thing people came to read.
 *
 * The manifest (paths, sizes, hashes) arrives with the events. Document
 * bodies do not: they are fetched one at a time on expand, keyed by content
 * hash, so opening a run never pulls a megabyte of markdown.
 */

/** Colour of the "System prompt" slice of the context meter, reused so the
 *  panel and the meter obviously describe the same thing. */
const SYSTEM_CATEGORY = CONTEXT_CATEGORIES.find((c) => c.id === "system");

/** Where a segment came from, in words an operator can act on. */
const SOURCE_LABELS = {
  static: "runtime",
  agent_md: "AGENT.md",
  identity: "frontmatter",
  memory_file: "tenant memory",
  provider: "memory provider",
  skill_md: "skill",
  skill_lessons: "lessons",
  runtime_string: "dispatch",
  tool_catalog: "tools",
};

/** Why a body could not be fetched. Distinct causes, distinct wording —
 *  "not archived" sent people looking at the runtime when the real problem
 *  was the run id the page asked with. */
const MISSING_REASONS = {
  not_recorded:
    "No archived body — this run predates prompt archiving, or archiving failed. Showing the recorded preview.",
  run_not_found:
    "Could not resolve this run's website, so the body could not be located. Showing the recorded preview.",
  invalid_sha256:
    "The manifest entry carries no usable content hash. Showing the recorded preview.",
  unknown: "Body unavailable — showing the recorded preview.",
};

function SlotBadge({ slot }) {
  const isUser = slot === "user";
  return (
    <span
      className={`shrink-0 rounded px-1 py-px text-[9px] font-semibold ${
        isUser ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
      }`}>
      {slot}
    </span>
  );
}

function SegmentRow({ segment, share, env, runId, duplicated }) {
  const [open, setOpen] = useState(false);
  const [fetchSegment, { data, isFetching, isError }] =
    useLazyGetRunContextSegmentQuery();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    // Fetch once, on first expand.
    if (next && !data && !isFetching) {
      fetchSegment({ env, runId, sha256: segment.sha256 });
    }
  };

  const kind = SOURCE_LABELS[segment.source_kind] || segment.source_kind;
  const body = data?.found ? data.text : null;

  return (
    <div className='border-t border-gray-100 first:border-t-0'>
      <button
        type='button'
        onClick={toggle}
        className='flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50'>
        <span className='w-3 shrink-0 select-none text-[10px] text-gray-400'>
          {open ? "▾" : "▸"}
        </span>
        <span className='min-w-0 flex-1 truncate text-[11px] text-gray-800'>
          {segment.label}
        </span>
        {segment.truncated && (
          <span
            className='shrink-0 rounded bg-red-100 px-1 py-px text-[9px] font-semibold text-red-700'
            title='This document was cut down before it was inlined — the agent saw less than the file holds.'>
            truncated
          </span>
        )}
        {duplicated && (
          <span
            className='shrink-0 rounded bg-amber-100 px-1 py-px text-[9px] font-semibold text-amber-800'
            title='The same text is also sent as a user message — it occupies two slots.'>
            also in user
          </span>
        )}
        <span className='shrink-0 text-[9px] uppercase tracking-wide text-gray-400'>
          {kind}
        </span>
        <SlotBadge slot={segment.slot} />
        <span className='w-16 shrink-0 text-right font-mono text-[10px] tabular-nums text-gray-500'>
          {formatTokenCount(segment.tokens)}
        </span>
        <span
          className='h-1.5 w-12 shrink-0 overflow-hidden rounded-full'
          style={{ background: SYSTEM_CATEGORY?.track }}
          aria-hidden>
          <span
            className='block h-full rounded-full'
            style={{
              width: `${Math.max(2, Math.min(100, share))}%`,
              background: SYSTEM_CATEGORY?.color,
            }}
          />
        </span>
      </button>

      {open && (
        <div className='px-3 pb-2'>
          {segment.source_path && (
            <div className='mb-1 font-mono text-[10px] text-gray-400'>
              {segment.source_path}
            </div>
          )}
          {isFetching && (
            <div className='text-[11px] text-gray-400'>loading…</div>
          )}
          {isError && (
            <div className='text-[11px] text-red-600'>
              could not load this document
            </div>
          )}
          {!isFetching && data && !data.found && (
            <div className='text-[11px] text-gray-500'>
              {MISSING_REASONS[data.reason] || MISSING_REASONS.unknown}
              <div className='mt-1 rounded bg-gray-50 p-2 text-[11px] text-gray-600'>
                {segment.preview}
              </div>
            </div>
          )}
          {body && (
            <div className='max-h-[320px] overflow-y-auto rounded border border-gray-200 bg-white p-2'>
              <ProseText
                text={body}
                className='whitespace-pre-wrap text-[11px] leading-relaxed text-gray-700'
              />
              {data.truncated && (
                <div className='mt-1 text-[10px] text-amber-700'>
                  showing the first {formatTokenCount(data.text.length)} of{" "}
                  {formatTokenCount(data.chars)} characters
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContextManifest({ manifest, env, runId }) {
  if (!manifest || !(manifest.segments || []).length) return null;

  const segments = manifest.segments;
  const total = manifest.total_tokens || 0;
  const duplicated = new Set(manifest.duplicated_in_user_slot || []);

  return (
    <details className='mt-2 rounded-lg border border-gray-200 bg-gray-50'>
      <summary className='flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
        <span>context</span>
        <span className='font-mono normal-case tracking-normal text-gray-400'>
          {segments.length} segments · {formatTokenCount(total)} tokens
        </span>
        {duplicated.size > 0 && (
          <span className='rounded bg-amber-100 px-1 py-px text-[9px] font-semibold normal-case tracking-normal text-amber-800'>
            {duplicated.size} in two slots
          </span>
        )}
        {manifest.archived === false && (
          <span className='rounded bg-gray-200 px-1 py-px text-[9px] font-semibold normal-case tracking-normal text-gray-600'>
            previews only
          </span>
        )}
      </summary>
      <div className='border-t border-gray-200 bg-white'>
        {segments.map((segment) => (
          <SegmentRow
            key={`${segment.order}:${segment.id}`}
            segment={segment}
            share={total > 0 ? (segment.tokens / total) * 100 : 0}
            env={env}
            runId={runId}
            duplicated={duplicated.has(segment.id)}
          />
        ))}
      </div>
    </details>
  );
}
